import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

function hasRole(user, roleName) {
  if (!user || !Array.isArray(user.roles)) return false;
  return user.roles.some((r) => r.role === roleName);
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser]             = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // true until startup check resolves

  // On mount: if there's a token in localStorage, validate it and rehydrate
  // the user profile. Any failure (401, network error) means not logged in.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    authService.getMe()
      .then(({ user: profile }) => {
        setUser(profile);
        setIsLoggedIn(true);
      })
      .catch((err) => {
        if (err.status === 401) {
          // Definitively invalid token — apiService already wiped it from
          // localStorage; nothing else to do here.
        }
        // Any other failure (network down, 5xx) is transient — leave the
        // token intact so the next page load can try again.
      })
      .finally(() => setAuthLoading(false));
  }, []);

  /**
   * Called by Login.js and CreateAccount.js after a successful
   * verifyLogin / verifyEmail to push the user into authenticated state.
   */
  const setSession = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
  };

  /**
   * Clear local session state without calling the logout endpoint.
   * Used when the server tells us the token is no longer valid.
   */
  const clearSession = () => {
    localStorage.removeItem('token');
    sessionStorage.setItem('session_expired', '1');
    setIsLoggedIn(false);
    setUser(null);
  };

  /**
   * Update the current user's personal info and refresh the in-memory user
   * object so geocoded coordinates stay current after an address change.
   */
  const updateUser = async (formData) => {
    await authService.updateUser(formData);
    const { user: refreshed } = await authService.getMe();
    setUser(refreshed);
  };

  /**
   * Re-fetch the current user from the server and update context state.
   * Call this after any operation that may have changed server-side user data
   * (e.g. a profile save that triggers re-geocoding).
   */
  const refreshUser = async () => {
    const { user: refreshed } = await authService.getMe();
    setUser(refreshed);
  };

  /**
   * Revoke the server-side token and clear local state.
   */
  const logout = async () => {
    await authService.logout(); // also removes token from localStorage
    setIsLoggedIn(false);
    setUser(null);
  };

  // Re-validate the token whenever the user switches back to this tab.
  // Catches expired/revoked tokens without polling on a timer.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isLoggedIn) return;
      authService.getMe().catch((err) => {
        // Only kill the session if the server explicitly rejected the token.
        // Network errors and 5xx responses are transient — don't log the user out.
        if (err.status === 401) clearSession();
      });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdmin = useMemo(() => hasRole(user, 'site_admin'), [user]);

  const value = {
    isLoggedIn,
    user,
    authLoading,
    isAdmin,
    hasRole: (roleName) => hasRole(user, roleName),
    setSession,
    clearSession,
    logout,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
